import {
  createBrowserRouter,
  Navigate,
  Outlet,
  type RouteObject,
} from 'react-router';

import { BaseLayout } from '@ll-platform/frontend/components/layouts/BaseLayout';
import { withLoader } from '@ll-platform/frontend/components/router/helpers';
import SuspenseLoader from '@ll-platform/frontend/components/SuspenseLoader';
import { ErrorPage } from '@ll-platform/frontend/components/statusPages/ErrorPage';
import { AppEnvEnum, appConfig } from '@ll-platform/frontend/config/app.config';
import { AuthRedirectController } from '@ll-platform/frontend/core/auth/guards/AuthRedirectController';
import { appRoutes } from '@ll-platform/frontend/features/router';
import { lazyLoad } from '@ll-platform/frontend/utils/router/lazyLoad';

const Status404 = withLoader(
  lazyLoad(
    () => import('@ll-platform/frontend/components/statusPages/Status404'),
  ).default,
);

const GoogleOAuthCallbackController = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/container/auth/pages/callbacks/google/GoogleOAuthCallbackController'
      ),
  ).GoogleOAuthCallbackController,
);

const LoginPage = withLoader(
  lazyLoad(
    () =>
      import('@ll-platform/frontend/features/auth/pages/login/pages/LoginPage'),
  ).LoginPage,
);

const RegisterPage = withLoader(
  lazyLoad(
    () =>
      import('@ll-platform/frontend/features/auth/pages/register/RegisterPage'),
  ).RegisterPage,
);

const ResetPasswordController = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/auth/pages/resetPassword/ResetPasswordController'
      ),
  ).ResetPasswordController,
);

const routes: RouteObject[] = [
  {
    path: '',
    element: <Outlet />,
    ErrorBoundary: [AppEnvEnum.Production, AppEnvEnum.Staging].includes(
      appConfig.REACT_APP_ENV,
    )
      ? ErrorPage
      : undefined,
    children: [
      {
        path: '',
        element: (
          <AuthRedirectController loadingComponent={<SuspenseLoader />} />
        ),
      },
      ...appRoutes,
      {
        path: 'login',
        element: <LoginPage />,
      },
      {
        path: 'register',
        element: <RegisterPage />,
      },
      {
        path: 'reset-password',
        element: <ResetPasswordController />,
      },
      {
        path: 'auth',
        children: [
          {
            path: 'google',
            children: [
              {
                path: 'callback',
                element: <GoogleOAuthCallbackController />,
              },
            ],
          },
        ],
      },
      {
        path: 'status',
        children: [
          {
            path: '',
            element: <Navigate to="404" replace />,
          },
          {
            path: '404',
            element: (
              <BaseLayout>
                <Status404 />
              </BaseLayout>
            ),
          },
        ],
      },
      {
        path: '*',
        element: (
          <BaseLayout>
            <Status404 />
          </BaseLayout>
        ),
      },
    ],
  },
];

export const mainRouter = createBrowserRouter(routes);
